import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Drawer from "../../components/drawer/drawer";
import Modal from "../../components/modal/modal";
import PageHeader from "../../components/page-header/page-header";
import { Model } from "../../types";
import { ModelForm, ModelTable } from "./components";
import { useModels } from "./hooks";
import { useEffectiveBranchId } from "../../hooks/use-effective-branch-id";
import { ModelManagementState } from "./types/models-types";
import {
  resetModelState,
  openEditDrawer,
  openCreateDrawer,
  closeDrawer,
  openDeleteModal,
  closeDeleteModal,
  getDrawerTitle,
  getPageTitle,
} from "./utils/models-utils";
import "./models.css";
import { toast } from "sonner";

const Models: React.FC = () => {
  const { brandId } = useParams<{ brandId: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<ModelManagementState>(resetModelState());

  // Get effective branch ID (user's branch for regular users, selected branch for super admin)
  const effectiveBranchId = useEffectiveBranchId();

  const {
    models,
    brands,
    isLoading,
    handleCreateModel,
    handleUpdateModel,
    handleDeleteModel,
    // Pagination from hook
    currentPage,
    itemsPerPage,
    totalPages,
    handlePageChange,
    handleItemsPerPageChange,
  } = useModels(effectiveBranchId);

  // Find current brand if viewing models for a specific brand
  const currentBrand = brandId ? brands.find((b) => b.id === brandId) : null;

  // TODO: Improve error handling and add proper response types
  // Event handlers
  const handleSubmit = async (values: Partial<Model>): Promise<void> => {
    let response: any;
    if (state.editingModel) {
      response = await handleUpdateModel(state.editingModel.id, values);
    } else {
      response = await handleCreateModel(values);
    }

    const hasSuccessField = typeof response?.success === "boolean";
    if (!hasSuccessField || response?.success) {
      toast.success(response?.message || "Операция выполнена успешно");
      setState(closeDrawer());
    } else {
      toast.error(response?.message || "Произошла ошибка");
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!state.deletingModel) return;

    const success = await handleDeleteModel(state.deletingModel.id);
    if (success) {
      setState(closeDeleteModal());
    }
  };

  const handleCreateDrawer = (): void => {
    setState(openCreateDrawer());
  };

  const handleEditDrawer = (model: Model): void => {
    setState(openEditDrawer(model));
  };

  const handleCloseDrawer = (): void => {
    setState(closeDrawer());
  };

  const handleDeleteModal = (model: Model): void => {
    setState(openDeleteModal(model));
  };

  const handleCloseDeleteModal = (): void => {
    setState(closeDeleteModal());
  };

  const handleBackToBrands = (): void => {
    navigate("/brands");
  };

  return (
    <div className="models">
      <PageHeader
        title={getPageTitle(currentBrand?.name)}
        subtitle="Управление моделями автомобилей"
        showBackButton={!!brandId}
        backButtonText="Назад к брендам"
        onBackClick={handleBackToBrands}
        actionButton={{
          text: "Добавить модель",
          icon: "plus",
          onClick: handleCreateDrawer,
        }}
      />

      <ModelTable
        models={models}
        brands={brands}
        isLoading={isLoading}
        onEdit={handleEditDrawer}
        onDelete={handleDeleteModal}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />

      <Drawer
        title={getDrawerTitle(state.editingModel)}
        isOpen={state.isDrawerOpen}
        onClose={handleCloseDrawer}
        width={500}
      >
        <ModelForm
          initialValues={state.editingModel}
          onSubmit={handleSubmit}
          onCancel={handleCloseDrawer}
          isLoading={isLoading}
          brands={brands}
          {...(brandId && { selectedBrandId: brandId })}
        />
      </Drawer>

      <Modal
        isOpen={state.isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        title="Подтвердите удаление"
        onConfirm={handleDelete}
      >
        <p>Вы уверены, что хотите удалить эту модель?</p>
        <p>
          <strong>{state.deletingModel?.name}</strong>
        </p>
      </Modal>
    </div>
  );
};

export default Models;
